/**
 * Where the research map's nodes and edges are drawn. Kept free of JSX so the geometry is tested on numbers:
 * groups as bands stacked top to bottom in the map's order, each band's nodes flowing left to right and
 * wrapping at the width, edges between node centres. A crowded map or a narrow column falls back to a list.
 */
import type { RecordAuthority, RecordStatus, ResearchGroupKind, ResearchMapNode, ResearchMapView, SourceStatus } from "../../../shared/wire/research.ts";

/** More nodes than this are listed, not drawn. */
export const MAX_GRAPH_NODES = 80;
/** A column narrower than this lists the map instead of drawing it. */
export const MIN_GRAPH_WIDTH = 240;

const NODE_H = 28;
const GAP_X = 10;
const GAP_Y = 10;
const BAND_HEAD = 26;
const BAND_PAD = 10;
const PAD_X = 12;
const MAX_NODE_W = 320;
const EMPTY_BAND_H = 44;
const LABEL_PAD = 24;

/** A width estimate without a canvas: wide for CJK, narrow for Latin. */
export function measureLabel(text: string): number {
	let w = 0;
	for (const ch of text) w += ch.charCodeAt(0) > 0x2e7f ? 13 : 7.2;
	return Math.ceil(w);
}

/** The longest prefix of `label` that fits `maxW`, with an ellipsis when it was cut. */
export function fitLabel(label: string, maxW: number, measure: (text: string) => number): string {
	if (measure(label) <= maxW) return label;
	const chars = [...label];
	let end = chars.length;
	while (end > 0 && measure(`${chars.slice(0, end).join("")}…`) > maxW) end--;
	return `${chars.slice(0, end).join("")}…`;
}

export interface PlacedResearchBand {
	kind: ResearchGroupKind;
	label: string;
	y: number;
	h: number;
	count: number;
}

export interface PlacedResearchNode {
	id: string;
	/** The label as drawn, cut to the node's width. */
	label: string;
	/** The whole label, for the title and the detail. */
	title: string;
	x: number;
	y: number;
	w: number;
	h: number;
	group: ResearchGroupKind;
	kind: string;
	state: RecordStatus;
	authority: RecordAuthority;
	sourceStatus: SourceStatus | null;
}

export interface PlacedResearchEdge {
	id: string;
	from: string;
	to: string;
	type: string;
	provenance: "material" | "unknown";
	x1: number;
	y1: number;
	x2: number;
	y2: number;
	kind: "down" | "up" | "side";
}

export type ResearchLayout =
	| { mode: "graph"; width: number; height: number; bands: PlacedResearchBand[]; nodes: PlacedResearchNode[]; edges: PlacedResearchEdge[] }
	| { mode: "list"; reason: "crowded" | "narrow"; bands: Array<{ kind: ResearchGroupKind; label: string; nodes: ResearchMapNode[] }> };

type MapShape = Pick<ResearchMapView, "groups" | "nodes" | "edges">;

function listOf(map: MapShape, reason: "crowded" | "narrow"): ResearchLayout {
	const byId = new Map(map.nodes.map((n) => [n.id, n]));
	return { mode: "list", reason, bands: map.groups.map((g) => ({ kind: g.kind, label: g.label, nodes: g.nodeIds.map((id) => byId.get(id)).filter((n): n is ResearchMapNode => Boolean(n)) })) };
}

export function layoutResearchMap(map: MapShape, width: number, measure: (text: string) => number = measureLabel): ResearchLayout {
	if (width < MIN_GRAPH_WIDTH) return listOf(map, "narrow");
	if (map.nodes.length > MAX_GRAPH_NODES) return listOf(map, "crowded");
	const byId = new Map(map.nodes.map((n) => [n.id, n]));
	const bands: PlacedResearchBand[] = [];
	const nodes: PlacedResearchNode[] = [];
	const maxW = Math.min(MAX_NODE_W, width - 2 * PAD_X);
	let y = 0;
	for (const g of map.groups) {
		const members = g.nodeIds.map((id) => byId.get(id)).filter((n): n is ResearchMapNode => Boolean(n));
		const top = y;
		let x = PAD_X;
		let rowY = top + BAND_HEAD;
		let rows = members.length ? 1 : 0;
		for (const n of members) {
			const w = Math.min(maxW, measure(n.label) + LABEL_PAD);
			if (x + w > width - PAD_X && x > PAD_X) {
				x = PAD_X;
				rowY += NODE_H + GAP_Y;
				rows++;
			}
			nodes.push({ id: n.id, label: fitLabel(n.label, w - LABEL_PAD, measure), title: n.label, x, y: rowY, w, h: NODE_H, group: g.kind, kind: n.kind, state: n.state, authority: n.authority, sourceStatus: n.sourceStatus });
			x += w + GAP_X;
		}
		const h = members.length ? BAND_HEAD + rows * (NODE_H + GAP_Y) + BAND_PAD : EMPTY_BAND_H;
		bands.push({ kind: g.kind, label: g.label, y: top, h, count: members.length });
		y = top + h;
	}
	const placed = new Map(nodes.map((n) => [n.id, n]));
	const edges: PlacedResearchEdge[] = [];
	for (const e of map.edges) {
		const a = placed.get(e.from);
		const b = placed.get(e.to);
		if (!a || !b) continue;
		const kind = a.y === b.y ? "side" : a.y < b.y ? "down" : "up";
		const ax = a.x + a.w / 2;
		const bx = b.x + b.w / 2;
		edges.push({
			id: e.id,
			from: e.from,
			to: e.to,
			type: e.type,
			provenance: e.provenance,
			x1: kind === "side" ? (ax < bx ? a.x + a.w : a.x) : ax,
			y1: kind === "side" ? a.y + a.h / 2 : kind === "down" ? a.y + a.h : a.y,
			x2: kind === "side" ? (ax < bx ? b.x : b.x + b.w) : bx,
			y2: kind === "side" ? b.y + b.h / 2 : kind === "down" ? b.y : b.y + b.h,
			kind,
		});
	}
	return { mode: "graph", width, height: y, bands, nodes, edges };
}

/** The ids within one edge of `id`, itself included: what a selection lights. */
export function neighboursOf(map: Pick<ResearchMapView, "edges">, id: string): Set<string> {
	const near = new Set([id]);
	for (const e of map.edges) {
		if (e.from === id) near.add(e.to);
		if (e.to === id) near.add(e.from);
	}
	return near;
}
